import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axiosInstance from '../AxiosInstance';
import { useAuth } from '../AuthContext';
import {
    readCachedIntegrationHealth,
    writeCachedIntegrationHealth
} from '../Utilities/integrationToolbarCache';
import {
    INTEGRATION_HEALTH_POLL_MS,
    overlayIntegrationHealthWhenOffline,
    shouldPollIntegrationHealth,
    type IntegrationHealthItem,
    type IntegrationHealthStatus
} from '../Utilities/integrationHealthUi';

export type { IntegrationHealthItem, IntegrationHealthStatus };

export function getIntegrationHealthLabel(item: IntegrationHealthItem): string {
    const detail = item.message?.trim() || (item.status === 'healthy' ? 'Connected' : 'Status unknown');
    return `${item.providerName}: ${detail}`;
}

function StatusIcon({ status }: { status: IntegrationHealthStatus }) {
    if (status === 'failed')
        return <ErrorIcon color="error" />;
    if (status === 'disabled')
        return <WarningAmberIcon color="warning" />;
    return <CheckCircleIcon color="success" />;
}

function readNavigatorOnLine() {
    return typeof navigator === 'undefined' || navigator.onLine;
}

const IntegrationHealthIndicator = forwardRef(function IntegrationHealthIndicator(_, ref) {
    const organisationId = useAuth()?.organisationId ?? null;
    const [items, setItems] = useState<IntegrationHealthItem[]>(() =>
        readCachedIntegrationHealth<IntegrationHealthItem>(organisationId));
    const [isOnline, setIsOnline] = useState(readNavigatorOnLine);
    const itemsRef = useRef(items);
    const organisationIdRef = useRef(organisationId);
    itemsRef.current = items;
    organisationIdRef.current = organisationId;

    const fetchHealth = useCallback(async () => {
        const requestedOrganisationId = organisationIdRef.current;
        if (!requestedOrganisationId)
            return;

        setIsOnline(readNavigatorOnLine());

        try {
            const response = await axiosInstance.get<IntegrationHealthItem[]>('/settings/integration-health');
            if (organisationIdRef.current !== requestedOrganisationId)
                return;

            const nextItems = Array.isArray(response.data) ? response.data : [];
            setItems(nextItems);
            writeCachedIntegrationHealth(requestedOrganisationId, nextItems);
        } catch {
            // Keep the last known icons visible if a refresh fails.
            // Browser offline overlay still applies from isOnline.
        }
    }, []);

    useImperativeHandle(ref, () => ({
        refresh: fetchHealth
    }));

    useEffect(() => {
        const shouldFetchNow = () => shouldPollIntegrationHealth(itemsRef.current);
        const handleOnline = () => {
            setIsOnline(true);
            if (shouldFetchNow())
                void fetchHealth();
        };
        const handleOffline = () => setIsOnline(false);
        const handleVisibility = () => {
            if (document.visibilityState === 'visible' && shouldFetchNow())
                void fetchHealth();
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [fetchHealth]);

    useEffect(() => {
        setItems(readCachedIntegrationHealth<IntegrationHealthItem>(organisationId));
        void fetchHealth();
    }, [fetchHealth, organisationId]);

    const shouldPoll = shouldPollIntegrationHealth(items);

    useEffect(() => {
        if (!shouldPoll)
            return;

        const intervalId = setInterval(() => {
            void fetchHealth();
        }, INTEGRATION_HEALTH_POLL_MS);

        return () => clearInterval(intervalId);
    }, [fetchHealth, shouldPoll]);

    const displayItems = overlayIntegrationHealthWhenOffline(items, isOnline);

    if (displayItems.length === 0)
        return null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {displayItems.map(item => {
                const label = getIntegrationHealthLabel(item);
                return (
                    <Tooltip key={item.providerId} title={label}>
                        <Box
                            component="span"
                            aria-label={label}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                p: 1
                            }}
                        >
                            <StatusIcon status={item.status} />
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
});

export default IntegrationHealthIndicator;
